import type { FeedEntry } from '@ottd/api';

import { tr } from '@/lib/locale';
import { DAY, dayLabel, minutesSince, startOfDay } from '@/lib/time-labels';

/*
 * The server log is read top-down as "what just happened", so it is split into
 * time blocks with a heading rather than run as one undifferentiated list
 * (prototype `Homepage.dc.html`). The labels themselves live in
 * `@/lib/time-labels`, shared with the relative timestamps on other screens.
 */

/** Below this, an entry is still "just now" rather than merely today's. */
const JUST_NOW_MINUTES = 15;

export { agoLabel, exactTime } from '@/lib/time-labels';

export interface FeedBlock {
  head: string;
  entries: FeedEntry[];
}

function headFor(iso: string | null, now: Date): string {
  const minutes = minutesSince(iso, now);
  if (minutes === null) return tr('common:feedBlocks.earlier');

  if (minutes < JUST_NOW_MINUTES) return tr('common:feedBlocks.justNow');

  const day = startOfDay(new Date(iso as string));
  const today = startOfDay(now);

  if (day === today) return tr('common:feedBlocks.earlierToday');
  if (day === today - DAY) return tr('common:feedBlocks.yesterday');

  return dayLabel(new Date(iso as string)).toUpperCase();
}

/**
 * Splits an already newest-first feed into consecutive blocks. Consecutive is
 * the point: a heading opens when the block changes, so the list is never
 * reordered and a block never appears twice.
 */
export function toBlocks(entries: FeedEntry[], now: Date = new Date()): FeedBlock[] {
  const blocks: FeedBlock[] = [];

  for (const entry of entries) {
    const head = headFor(entry.time, now);
    const current = blocks.at(-1);

    if (current?.head === head) {
      current.entries.push(entry);
    } else {
      blocks.push({ head, entries: [entry] });
    }
  }

  return blocks;
}
